import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';

type Alignment = 'left' | 'center' | 'right' | null;
type Formats = ('bold' | 'italic' | 'underlined')[];
type Color = 'standard' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
type Size = 'small' | 'medium' | 'large';
type Orientation = 'horizontal' | 'vertical';

const ToggleButtonShowcase = () => {
  // Single selection example (alignment)
  const [alignment, setAlignment] = useState<Alignment>('left');

  // Multiple selection example (text formatting)
  const [formats, setFormats] = useState<Formats>(['bold']);

  // Properties
  const [color, setColor] = useState<Color>('primary');
  const [size, setSize] = useState<Size>('medium');
  const [orientation, setOrientation] = useState<Orientation>('horizontal');
  const [disabled, setDisabled] = useState(false);
  const [fullWidth, setFullWidth] = useState(false);

  const handleAlignment = (event: React.MouseEvent<HTMLElement>, newAlignment: Alignment) => {
    setAlignment(newAlignment);
  };

  const handleFormat = (event: React.MouseEvent<HTMLElement>, newFormats: Formats) => {
    setFormats(newFormats);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        ToggleButton Component
      </Typography>
      <Typography variant="body1" paragraph>
        The ToggleButton and ToggleButtonGroup components allow users to select one or multiple options from a group of
        choices.
      </Typography>

      {/* Preview */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Preview
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            width: '100%',
          }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Single Selection (Exclusive)
            </Typography>
            <ToggleButtonGroup
              value={alignment}
              exclusive /* Always exclusive for single selection */
              onChange={handleAlignment}
              disabled={disabled}
              orientation={orientation}
              color={color}
              size={size}
              fullWidth={fullWidth}
              sx={{ width: '100%' }}>
              <ToggleButton value="left" aria-label="left aligned">
                First Example
              </ToggleButton>
              <ToggleButton value="center" aria-label="centered">
                Second Example
              </ToggleButton>
              <ToggleButton value="right" aria-label="right aligned">
                Third Example
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Multiple Selection
            </Typography>
            <ToggleButtonGroup
              value={formats}
              onChange={handleFormat}
              disabled={disabled}
              orientation={orientation}
              color={color}
              size={size}
              fullWidth={fullWidth}
              sx={{ width: '100%' }}>
              <ToggleButton value="bold" aria-label="bold">
                <FormatBoldIcon />
              </ToggleButton>
              <ToggleButton value="italic" aria-label="italic">
                <FormatItalicIcon />
              </ToggleButton>
              <ToggleButton value="underlined" aria-label="underlined">
                <FormatUnderlinedIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Color</InputLabel>
            <Select value={color} label="Color" onChange={(e) => setColor(e.target.value as Color)}>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Size</InputLabel>
            <Select value={size} label="Size" onChange={(e) => setSize(e.target.value as Size)}>
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Orientation</InputLabel>
            <Select
              value={orientation}
              label="Orientation"
              onChange={(e) => setOrientation(e.target.value as Orientation)}>
              <MenuItem value="horizontal">Horizontal</MenuItem>
              <MenuItem value="vertical">Vertical</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Switch checked={disabled} onChange={(e) => setDisabled(e.target.checked)} />}
            label="Disabled"
          />
          <FormControlLabel
            control={<Switch checked={fullWidth} onChange={(e) => setFullWidth(e.target.checked)} />}
            label="Full Width"
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Usage Example:
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
            }}>
            {`<ToggleButtonGroup
  value={value}
  exclusive={true}  // Use for single selection only
  onChange={handleChange}
  color="${color}"
  size="${size}"
  orientation="${orientation}"
  disabled={${disabled}}
  fullWidth={${fullWidth}}
>
  <ToggleButton value="option1">Option 1</ToggleButton>
  <ToggleButton value="option2">Option 2</ToggleButton>
  <ToggleButton value="option3">Option 3</ToggleButton>
</ToggleButtonGroup>`}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ToggleButtonShowcase;
